import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { IApiTypedResult, IBusinessOperationQualifier, ILLGenericKeys, ITermByIndustryUsageAndCategory, Order, OrderUser, User } from '../../shared/models/index';
import { Media } from '../../shared/models/media.model';
import { AuthorizationService, OrderService, StorageManagerService } from '../../shared/services/index';
import { Utilities } from '../../shared/services/utilities';
import { OrderAdminComponent } from '../order-admin/order-admin.component';
import { fadeInOut } from '../../shared/helpers/animations';

@Component({
    selector: 'order-step-3',
    templateUrl: './order-step-3.component.html',
    styleUrls: ['./order-step-3.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [fadeInOut],
})
export class OrderStep3Component implements OnInit, OnDestroy, AfterViewInit {

    // Primary key
    orderRevisionId: number;
    // Object model for Order
    order: Order;
    // Hold on to original values for these fields for saving later
    beforeOrderStatus: string;
    beforeConfirmedDate: Date;
    // Angular form that contains controls
    orderStep3Form: UntypedFormGroup;
    // Show textboxes for changes
    showTextboxesForChanges = true;

    // Reference data list
    serviceCategoryToServiceTypeAssoc: IBusinessOperationQualifier[];
    // Full list of all service categories
    serviceCategories: ILLGenericKeys[];
    // Should we show description box
    showOrderDescription = true;

    // List of all employees in company
    allEmployeesInCompany: User[] = null;

    // Save button caption
    saveButtonCaption = 'Save as Draft';

    // Message when submitting
    submissionMessage = `Your order request has been submitted. One of our Project Coordinators is in the process of reviewing your order. Once the order has been reviewed you will receive a confirmation email that includes the expected delivery date.`;


    // ------------------------------------------------------------
    // Order admin box
    // ------------------------------------------------------------
    @ViewChild('orderAdminComponent')
    orderAdmin: OrderAdminComponent;

    // Delivery doc associated with this order
    // (not the source docs)
    deliveryDoc: Media = null;

    // If user deletes an existing delivery doc,
    // there's no angular form changes, but we need to force
    // a page reload.
    deliveryDocStatusChange: boolean;
    // No source docs on step 3

    orderStatuses: ITermByIndustryUsageAndCategory[] = null;

    // ------------------------------------------------------------
    // Page state
    // ------------------------------------------------------------
    // Are we currently loading or saving?
    pageState: string = null;
    // Can the current user edit the current order?
    canEdit: boolean;
    // Does the current user have admin order (RT employee) permissions
    canOrderAdmin: boolean;
    // Can user request cancellation?
    canRequestCancellation: boolean;
    // Error message to display on bottom of screen.
    errorMessage = '';
    canSubmitOrder: boolean;

    // ------------------------------------------------------------
    // Post save settings
    // ------------------------------------------------------------
    // Save the order object, when finished then optionally finish a deliverDoc
    // upload.  There's a lot of async steps, so put information about
    // saving orders and docs into class variables that can be set
    // in saveData and accessed in postSaveData to tie everything together.

    // Message from server about saving the order
    postSaveDataMessage: string;
    // URL to go to after save is finished.  Note we need the
    // new OrderRevisionId of the newly saved order revision
    // to generate this URL (usually).
    postSaveDataRedirectUrl: string;
    // OrderRevisionId from after we save order changes (but we haven't uploaded files yet so we need this stored)
    postSaveDataNewOrderRevisionId: number;
    // Track if source docs are finished uploading after order save
    postSaveWaitingForSourceDocUpload: boolean;
    // Track if delivery docs are finished uploading after order save
    postSaveWaitingForDeliveryUpload: boolean;
    ingestFileValue: boolean;


    constructor(private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private authorizationService: AuthorizationService,
        public orderService: OrderService,
        private storageManagerService: StorageManagerService,
        private toastr: ToastrService,
        private router: Router,
        private utilService: Utilities) { }


    // ------------------------------------------------------------
    // Initialize
    // ------------------------------------------------------------

    ngOnInit(): void {
        this.pageState = this.orderService.PAGE_STATE_LOADING;

        // Users can't change companies in the middle of viewing/editing an order
        this.utilService.toggleClientDropdownState(true);

        // Force some permissions off in case of ng routing doesn't do a full refresh
        this.order = null;
        this.beforeOrderStatus = null;
        this.beforeConfirmedDate = null;
        this.canEdit = false;
        this.canOrderAdmin = false;
        this.canRequestCancellation = false;
        this.deliveryDoc = null;
        this.deliveryDocStatusChange = false;
        // no source docs on step 3
        this.canSubmitOrder = false;

        // Extract primary key from query string
        this.orderRevisionId = this.orderService.getNumberOrNull(this.activatedRoute.snapshot.params.orderRevisionId);

        // The guard says you need read permission to get to this page.
        // User might also have edit permissions.
        this.canEdit = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);
        this.canOrderAdmin = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN);

        // Tell angular form what fields it has. None of these fields are required.
        this.orderStep3Form = this.fb.group({
            colOrDrugFactChangeInstruction: [''],
            principalDisplayPanelChangeInstruction: [''],
            LLMetadataChangeInstruction: [''],
            comments: [''],
        });

        // set focus on the invisible div so the firest tab will bring it to the "Skip to Main Navigation" button because it must be the first tab target on the page (508 Compliance)
       this.initTabIndex();

        // Get order data from the resolver and populate angular objects
        this.loadData();

        // Only show the three textboxes for changes
        // if order type full or partial (not ESG)
        this.showTextboxesForChanges = (this.order.serviceCategory != this.orderService.SERVICE_CATEGORY_ESG);

        this.populateFormControls();

        this.pageState = this.orderService.PAGE_STATE_READY;
    }

    initTabIndex() {
      (document.activeElement as HTMLElement).blur();

      const main = document.getElementById('firstDiv');

      if (main) {
        main.focus();
      }
    }

    // ------------------------------------------------------------
    // Load data
    // ------------------------------------------------------------

    // Get data from the resolver and populate various dropdownlists
    // and main order fields and store it in class state.
    loadData(): void {
        // Get data from resolver used in routing module
        const orderDataResolved = this.activatedRoute.snapshot.data['orderDataResolved'];
        if (orderDataResolved.error) {
            this.toastr.error(orderDataResolved.error);
        } else {
            if (orderDataResolved) {
                // OrderResolver returns a fixed array of data.

                // Employees of this company
                if (!orderDataResolved[0]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get list of employees in company', false);
                }
                this.allEmployeesInCompany = orderDataResolved[0];

                // Reference lists
                if (!orderDataResolved[1]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get reference data (service type)', false);
                }
                this.serviceCategoryToServiceTypeAssoc = orderDataResolved[1];
                // Main order
                if (!orderDataResolved[3]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order', false);
                }

                let orderApiResult: IApiTypedResult<Order> = orderDataResolved[3];
                if (orderApiResult) {
                    if (orderApiResult.isSuccess) {
                        this.order = orderApiResult.data;
                        // Remember original values for certain fields
                        this.beforeOrderStatus = this.order.orderStatus;
                        this.beforeConfirmedDate = this.order.confirmedDueDate;

                        // Only user with "SSDP.CanUpateOrder" permission can edit an order if the order status is "Order Delivered - Awaiting Approval" or "completed"
                        if (!this.canOrderAdmin && (this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
                            || this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_COMPLETE)) {
                            this.canEdit = false;
                        }
                        // Don't allow canOrderAdmin if draft
                        if (this.order.orderStatus === this.orderService.ORDER_STATUS_DRAFT) {
                            this.canOrderAdmin = false;
                        }

                        // If still canOrderAdmin, recaption save
                        if (this.canOrderAdmin) {
                            this.saveButtonCaption = 'Update Order';
                        }

                        // Non-admin users can cancel pending or inprocess if they have the perm
                        if (!this.canOrderAdmin
                            && ([this.orderService.ORDER_STATUS_PENDING, this.orderService.ORDER_STATUS_IN_PROCESS].indexOf(this.order.orderStatus) > -1)
                            && this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_CANCEL_ORDER)) {
                            this.canRequestCancellation = true;
                        }

                        // Can only submit if it's draft
                        if (this.order.orderStatus === this.orderService.ORDER_STATUS_DRAFT) {
                            this.canSubmitOrder = true;
                        }

                        // Pull out the deliveryDoc and sourceDocs separately
                        // order.sourceDocuments is an array mixed with both deliveryDoc and SourceDocs.
                        if (this.order && this.order.sourceDocuments) {
                            let deliveryDocs = this.order.sourceDocuments.filter(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_DELIVERY);
                            if (deliveryDocs && deliveryDocs.length > 0) {
                                this.deliveryDoc = deliveryDocs[0];
                            }
                            // Step 3 - no sourceDocs
                        }

                    } else {
                        this.orderService.handleOrderError(this, orderApiResult, 'Failed to load order', false);
                    }
                }

                if (!orderDataResolved[5]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order statuses', false);
                }
                this.orderStatuses = orderDataResolved[5];

                if (!orderDataResolved[6]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load service categories', false);
                }
                this.serviceCategories = orderDataResolved[6];
            }
        }
    } // end loadData


    ngAfterViewInit(): void {
        if (this.order && this.orderAdmin) {
            if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_FULL_CONVERSION  || this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_PARTIAL_CONVERSION) {
                this.orderAdmin.showIngestFile(true);
            } else {
                this.orderAdmin.showIngestFile(false);
            }
        }
    }

    // ------------------------------------------------------------
    // Populate form controls
    // ------------------------------------------------------------

    // Populate the order data
    populateFormControls(): void {
        this.orderStep3Form.reset();
        this.orderStep3Form.patchValue({
            colOrDrugFactChangeInstruction: this.order.colOrDrugFactChangeInstruction,
            principalDisplayPanelChangeInstruction: this.order.principalDisplayPanelChangeInstruction,
            LLMetadataChangeInstruction: this.order.LLMetadataChangeInstruction,
            comments: this.order.additionalInformation
        });
    }

    onIngestFilechanged(event: boolean) {
        this.ingestFileValue = event;
    }

    // ------------------------------------------------------------
    // Order admin is a subcomponent, and emits up certain events
    // usually about the delivery doc
    // ------------------------------------------------------------
    onOrderAdminFileUpload(event) {
        if (event === 'success') {
            // Successfully uploaded delivery doc
            this.postSaveWaitingForDeliveryUpload = false;
            this.postSaveData();
        } else if (event === 'failure') {
            // Attempted to upload delivery doc but failed
            this.pageState == this.orderService.PAGE_STATE_READY;
            this.toastr.error('Unexpected error uploading delivery document');
        } else if (event === 'deleteExistingDeliveryDoc') {
            // Request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = true;
                this.deliveryDoc.isDeleted = true;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;
        } else if (event === 'undeleteExistingDeliveryDoc') {
            // Undo request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = false;
                this.deliveryDoc.isDeleted = false;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;
        } else if (event === 'onIngestFileChange') {
            
            if (this.deliveryDoc) {
                this.deliveryDoc.ingestFile = this.ingestFileValue;
            }
            this.pageState == this.orderService.PAGE_STATE_READY;

        }
    }


    // ------------------------------------------------------------
    // Submission validation
    // ------------------------------------------------------------

    // Before official order submission, validate additional fields
    // Regular "save as draft" doesn't do this level of validation.
    validateForSubmission() {
        let message = '';
        let isValid = true;

        // Require order type
        if (!this.order.splService) {
            isValid = false;
            message += 'Order type (step 1) cannot be blank.  ';
        }

        // Require order name
        if (!this.order.orderName) {
            isValid = false;
            message += 'Order name (step 1) cannot be blank.  ';
        }

        // Require delivery priority
        if (this.order && !this.order.deliveryPriority) {
            isValid = false;
            message += 'Delivery priority (step 1) cannot be blank.  ';
        }

        // Require at least one order owner
        let orderOwners: OrderUser[] = [];
        if (this.order.orderUsers) {
            orderOwners = this.order.orderUsers.filter(x => x.orderUserTypeId === this.orderService.ORDER_USER_TYPE_ORDER_OWNER);
        }
        if (!orderOwners || (orderOwners.length == 0)) {
            isValid = false;
            message += 'At least one order owner (step 1) must be supplied.  ';
        }

        // Note: data collection form and baseline SPL are never required for submission.

        if (!isValid) {
            message = 'Unable to submit order.  Please ensure required data is supplied before submitting order.  ' + message;
            // turn off spinner before message
            this.pageState = this.orderService.PAGE_STATE_READY;
            this.orderService.alert('Order Submission Failed', message);
        }
        return isValid;
    }

    // ------------------------------------------------------------
    // Save data
    // ------------------------------------------------------------
    saveData(nextPage: string) {

        // Special case - "Submit Order" has additional validation rules.
        // They apply for fields that are on step 3.
        // Keep this code on all pages for consistency.
        if (nextPage === 'SUBMIT') {
            if (!this.validateForSubmission()) {
                return;
            }
        }

        this.pageState = this.orderService.PAGE_STATE_SAVING;

        // Clear out data from prior save
        this.postSaveDataMessage = null;
        this.postSaveDataNewOrderRevisionId = null;
        this.postSaveDataRedirectUrl = null;
        this.postSaveWaitingForDeliveryUpload = false;
        this.postSaveWaitingForSourceDocUpload = false;

        // Where to go after save
        let nextPageUrl = '';
        switch (nextPage) {
            case 'STEP2':
                nextPageUrl = '/order-step-2';
                break;
            case 'STEP3':
                nextPageUrl = '/order-step-3';
                break;
            case 'SUBMIT':
                nextPageUrl = '/order-tracker';
                break;
        }

        this.errorMessage = '';

        const modifiedByUser = this.storageManagerService.read('userData').name;

        // Request order cancellation is different enough let's handle it separately.
        if (nextPage === 'CANCEL') {
            this.orderService.requestOrderCancellation(this.canRequestCancellation, this.order, modifiedByUser, '/order-step-1', false);
            this.pageState = this.orderService.PAGE_STATE_READY;
        } else {
            let dirty = this.orderService.isAngularFormDirty(this.orderStep3Form);
            // Also check fields in order admin box
            if (this.canOrderAdmin && this.orderAdmin.orderAdminForm) {
                dirty = dirty || this.orderService.isAngularFormDirty(this.orderAdmin.orderAdminForm);
            }

            // stop here if form is invalid
            if (this.orderStep3Form.invalid) {
                this.pageState = this.orderService.PAGE_STATE_READY;
                this.toastr.warning('Data is invalid.');
                return;
            } else {
                // Only save if dirty (but you can always submit even if not dirty)
                if ((dirty || nextPage === 'SUBMIT') && (this.canEdit || this.orderAdmin)) {

                    // Put back in prior values for certain fields
                    this.order.beforeOrderStatus = this.beforeOrderStatus;
                    this.order.beforeConfirmedDate = this.beforeConfirmedDate;

                    // Before we save any data, check if there are files waiting to be uploaded
                    let sourceDocsWaitingToBeUploaded = false;
                    // no source docs on step 3
                    let deliveryDocsWaitingToBeUploaded = false;
                    if (this.canOrderAdmin && this.orderAdmin.adminFileUpload && this.orderAdmin.adminFileUpload.getFilesData() && this.orderAdmin.adminFileUpload.getFilesData().length > 0) {
                        deliveryDocsWaitingToBeUploaded = true;
                    }

                    // Save the order attributes first
                    this.order.colOrDrugFactChangeInstruction = this.f.colOrDrugFactChangeInstruction.value;
                    this.order.principalDisplayPanelChangeInstruction = this.f.principalDisplayPanelChangeInstruction.value;
                    this.order.LLMetadataChangeInstruction = this.f.LLMetadataChangeInstruction.value;
                    this.order.additionalInformation = this.f.comments.value;

                    // Special case - if service category is ESG, textboxes for changes should be hidden
                    // so force blanks.
                    if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_ESG) {
                        this.order.colOrDrugFactChangeInstruction = '';
                        this.order.principalDisplayPanelChangeInstruction = '';
                        this.order.LLMetadataChangeInstruction = '';
                    }

                    // Special case for submit
                    if (nextPage === 'SUBMIT') {
                        this.order.orderStatus = this.orderService.ORDER_STATUS_PENDING;
                    }

                    // Save the admin fields if we're allowed
                    if (this.order && this.canOrderAdmin) {
                        this.order.orderStatus = this.orderAdmin.orderAdminForm.controls.orderStatus.value;
                        this.order.confirmedDueDate  = this.utilService.getLocalDateString(this.orderAdmin.orderAdminForm.controls.confirmedDueDate.value);
                        if (deliveryDocsWaitingToBeUploaded) {
                            // DeliveryDoc supplied, mark the actual delivery date
                            this.order.actualDeliveryDate = this.utilService.getLocalDateString(new Date());
                        } else if (this.deliveryDoc && this.deliveryDoc.isDeleted) {
                            // DeliveryDoc was requested to be deleted, blank
                            // out actual delivery date
                            this.order.actualDeliveryDate = null;
                        }
                    }

                    // Save to database
                    this.orderService.saveOrder(this.order, modifiedByUser).subscribe((response) => {
                        // Coerce to specific type
                        const responseTyped: IApiTypedResult<Order> = response as IApiTypedResult<Order>;
                        if (responseTyped.isSuccess) {
                            // Gather up settings so after async upload finishes we know where to go
                            this.postSaveDataMessage = responseTyped.message;
                            this.postSaveDataNewOrderRevisionId = responseTyped.data.orderRevisionId;
                            this.postSaveDataRedirectUrl = `${nextPageUrl}/${this.postSaveDataNewOrderRevisionId}`;

                            if (nextPage === 'SUBMIT') {
                                // Show a nice message about submission and go to order tracker.
                                // Skip toaster since we have a popup.

                                // turn off spinner before message
                                this.pageState = this.orderService.PAGE_STATE_READY;
                                this.orderService.alert('Order Submission Successful', this.submissionMessage, () => {
                                    this.router.navigate(['/order-tracker']).then(() => {
                                        this.pageState = this.orderService.PAGE_STATE_READY;
                                        window.scrollTo(0, 0);
                                    })
                                });
                            } else {
                                // Regular save of Order object successful, now do optional file uploads.
                                //
                                // We just set the property on this component postSavedatNewOrderRevisionId
                                // which is shared with the child component OrderAdminComponent,
                                // sounds great BUT angular timing messes it up.  The child component
                                // can't see the value immediately.  The child component can't do setTimeout
                                // because the upload is already in progress.  So, we make the parent setTimeout
                                // (sleep) right now so angular child component catches up with bound value for
                                // and can save the uploaded doc to correct postSavedatNewOrderRevisionId
                                setTimeout(() => {

                                    // Now that the order is saved, upload docs to *new* OrderRevisionId from
                                    // potentially sourceDocs and delivery (inside child component)
                                    if (!sourceDocsWaitingToBeUploaded && !deliveryDocsWaitingToBeUploaded) {
                                        // No files to upload, we're done.
                                        this.postSaveData();
                                    } else {
                                        // no sourceDoc files from this page
                                        // Asynchronously upload delivery file from admin child componet
                                        if (deliveryDocsWaitingToBeUploaded) {
                                            // upload the files
                                            this.postSaveWaitingForDeliveryUpload = true;
                                            this.orderAdmin.adminFileUpload.upload(this.orderAdmin.adminFileUpload.getFilesData());  // this is async
                                        }
                                        // Nothing left for now, wait for events to get triggered.
                                        return;
                                    }
                                }, 100);
                            }
                        } else {
                            this.orderService.handleOrderError(this, responseTyped, 'Order save failed', true)
                        }
                    },
                        error => this.orderService.handleOrderError(this, error, 'Order save failed', true)
                    );
                    this.orderStep3Form.markAsPristine();
                } else {
                    this.pageState = this.orderService.PAGE_STATE_READY;
                    // if no changes to save, but user wants to go to next page, that's fine
                    if (nextPage !== 'STEP3') {
                        // Force angular to rerun ngOnInit to reload data when navigating to address
                        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
                        this.router.onSameUrlNavigation = 'reload';

                        this.router.navigate([`${nextPageUrl}/${this.orderRevisionId}`]).then(() => {
                            this.pageState = this.orderService.PAGE_STATE_READY;
                            window.scrollTo(0, 0);
                        });
                    } else {
                        this.pageState = this.orderService.PAGE_STATE_READY;
                        this.toastr.warning('No changes to save.');
                    }
                }
            }
        }
    }

    // After order is saved AND all files are uploaded via order admin
    // and Step 2 file uploader (not easy because they are all async)
    // then go ahead and give a message and redirect.
    // Only step 2 has dual file uploaders - source & delivery,
    // but for consistency I'm copy/pasting this function across
    // all three steps.
    postSaveData() {
        // Are we still waiting for sourceDoc upload or delivery upload?
        if (this.postSaveWaitingForSourceDocUpload || this.postSaveWaitingForDeliveryUpload) {
            // Yup, keep waiting.
            return;
        }

        this.toastr.success(this.postSaveDataMessage);

        // Force angular to rerun ngOnInit to reload data when navigating to address
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        this.router.navigate([this.postSaveDataRedirectUrl]).then(() => {
            this.pageState = this.orderService.PAGE_STATE_READY;
            // Clear out admin file upload control if relevant
            if (this.orderAdmin && this.orderAdmin.adminFileUpload) {
                this.orderAdmin.adminFileUpload.clearAll();
            }
            window.scrollTo(0, 0);
        });
    }


    // ------------------------------------------------------------
    // Misc
    // ------------------------------------------------------------

    // convenience getter for easy access to form fields
    get f() { return this.orderStep3Form.controls; }

    // ------------------------------------------------------------
    // Destroy
    // ------------------------------------------------------------
    ngOnDestroy(): void {
        this.utilService.toggleClientDropdownState(false);
    }
}
